import { PaymentModuleId } from '@foundation/apis/payments';
import { localizedCountryName } from '@foundation/countries';
import { SupportedLanguage } from '@foundation/localization';
import Clarity from '@microsoft/clarity';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import { useEffect, useMemo, useState } from 'react';

import {
  ArrowIcon,
  BoldHeading,
  disableUIBlocker,
  PageLayout,
  SimpleButton,
} from '@/components/common';
import { AlertWarning } from '@/components/common/alerts';
import { RedirectStatus } from '@/components/contexts/params';
import { PayButton } from '@/components/pages/stored-instruments/utils/PayButton';
import { useUserFriendlyPaymentModuleName } from '@/hooks/useUserFriendlyPaymentModuleName';
import { triggerErrorToast } from '@/libs/toasts';
import { IMandator } from '@/schemas/Mandator';
import LSVService from '@/services/directdebit/LSVService';
import Logger, { LoggerOrigin } from '@/services/Logger';
import { getMandatorInformation } from '@/services/mandators/MandatorsApi';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';
import { formatPrice } from '@/utils/formatters';

export function LsvMandateSummaryPage() {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const paymentModuleT = useUserFriendlyPaymentModuleName();

  const [error, setError] = useState<Error>();
  const [mandator, setMandator] = useState<IMandator>();
  const { accessToken, userId } = useAuth();
  const customerNumber = userId;

  if (PaymentStore.legalEntity === undefined) {
    throw new Error('legal entity should be set');
  }

  useEffect(() => {
    disableUIBlocker();

    if (PaymentStore.legalEntity === undefined) {
      throw new Error('legal entity should be set');
    }

    getMandatorInformation(PaymentStore.legalEntity, accessToken ?? '')
      .then((mandatorInfo) => {
        setMandator(mandatorInfo);
      })
      .catch((e: Error) => {
        TelemetryService.trackException(e, 'LsvMandateSummaryPage');
        Logger.error(
          LoggerOrigin.ReactComponent,
          'Mandator info fetch failure',
          e,
        );
        setError(e);
      });
  }, []);

  const styles = {
    pageContentHeader:
      'sticky top-0 bg-primary p-sm md:p-md 2xl:p-lg z-50 text-white',
    container: 'flex gap-4 relative focused-container items-start',
    checkbox: 'mt-2 ml-1 text-12 scale-125',
  };

  const headerText = useMemo(() => {
    return t('Total: {{totalPrice}}', {
      totalPrice: formatPrice(
        language as SupportedLanguage,
        PaymentStore.currency,
        PaymentStore.orderTotal,
      ),
    });
  }, [t, language]);

  if (error) {
    return (
      <AlertWarning
        title={t('Something went wrong!')}
        description={t('LSV payments are currently unavailable.')}
      />
    );
  }

  return (
    <PageLayout
      headerDescription={
        PaymentStore.orderNumber
          ? t('Order: {{orderNumber}}', {
              orderNumber: PaymentStore.orderNumber,
            })
          : undefined
      }
    >
      <div className={styles.pageContentHeader}>
        <BoldHeading>{headerText}</BoldHeading>
      </div>
      <div className="m-sm md:m-md">
        <div>
          <SimpleButton
            onClick={() =>
              NavigationService.navigateTo({
                page: InternalPage.PaymentMethods,
              })
            }
          >
            <span className={'mt-2 flex flex-row items-center justify-center'}>
              <ArrowIcon direction="left" />
              <span className={'ml-xs'}>{t('Back')}</span>
            </span>
          </SimpleButton>
        </div>
        <br />
        <b>{t('Account Holder')}</b>
        <table>
          <thead>
            <tr>
              <td className="table-cell">{t('First name/Last Name')}</td>
              <td className="table-cell">{PaymentStore.customerName}</td>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="table-cell">{t('Customer Number')}</td>
              <td className="table-cell">{customerNumber}</td>
            </tr>
            <tr>
              <td className="table-cell">{t('Email')}</td>
              <td className="table-cell">{PaymentStore.customerEmail}</td>
            </tr>
            <tr>
              <td className="table-cell">{t('Street and house number')}</td>
              <td className="table-cell">{PaymentStore.customerStreet}</td>
            </tr>
            <tr>
              <td className="table-cell">{t('ZIP/City')}</td>
              <td className="table-cell">{PaymentStore.customerCity}</td>
            </tr>
            <tr>
              <td className="table-cell">{t('Country or Region')}</td>
              <td className="table-cell">
                {localizedCountryName(
                  PaymentStore.customerCountryCode,
                  language,
                )}
              </td>
            </tr>
            <tr>
              <td className="table-cell">{t('IBAN')}</td>
              <td className="table-cell">
                {PaymentStore.directDebitMandate?.iban}
              </td>
            </tr>
          </tbody>
        </table>
        <br />
        {mandator && (
          <div>
            <b>{t('Payee')}</b>
            <table>
              <thead>
                <tr>
                  <td className="table-cell">{t('Company')}</td>
                  <td className="table-cell"> {mandator.companyName}</td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="table-cell">{t('Creditor ID')}</td>
                  <td className="table-cell">{mandator.iban}</td>
                </tr>
                <tr>
                  <td className="table-cell">{t('Street and house number')}</td>
                  <td className="table-cell">{mandator.addressline1}</td>
                </tr>
                <tr>
                  <td className="table-cell">{t('ZIP/City')}</td>
                  <td className="table-cell">
                    {mandator.zip} {mandator?.locality}
                  </td>
                </tr>
                <tr>
                  <td className="table-cell">{t('Country or Region')}</td>
                  <td className="table-cell">
                    {localizedCountryName(mandator.country ?? '', language)}
                  </td>
                </tr>
                <tr>
                  <td className="table-cell">{t('Mandate type')}</td>
                  <td className="table-cell">
                    {t('LSV basic mandate for repeated use')}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        <br />
        <div>
          {t(
            'To use the LSV payment method, please, download and fill the LSV-form. Upon filling send it to your bank and {{companyName}}.',
            {
              companyName: mandator?.companyName,
            },
          )}
        </div>
        <br />
        <a
          className="text-primary underline"
          href={import.meta.env.VITE_PAYMENTS_DIRECTDEBIT_FORM}
          target="_blank"
          rel="noreferrer"
        >
          {t('Download LSV authorization form')}
        </a>
        <br />
        <br />
        <div className={'flex flex-row items-center justify-center wrap'}>
          <br />
          {
            <PayButton
              title={t('Proceed')}
              onClick={() => {
                const currentPage = NavigationService.getInternalPage();

                NavigationService.navigateTo({
                  page: InternalPage.StatusProcessing,
                });
                const lsvMandate = PaymentStore.directDebitMandate;

                if (lsvMandate === undefined) {
                  throw new Error('LSV mandate information is missing');
                }

                LSVService.submitLsvForm(
                  lsvMandate.customerName,
                  lsvMandate.iban,
                  lsvMandate.bankName ?? '',
                  accessToken ?? '',
                )
                  .then(() => {
                    NavigationService.navigateTo({
                      page: InternalPage.StatusSuccess,
                    });
                    Clarity.setTag('pay', [
                      RedirectStatus.Success,
                      paymentModuleT(PaymentModuleId.DirectDebitLSV),
                    ]);
                  })
                  .catch((err) => {
                    NavigationService.navigateTo(currentPage);
                    TelemetryService.trackException(
                      err,
                      'LsvMandateSummaryPage',
                    );
                    Logger.error(
                      LoggerOrigin.ReactComponent,
                      'Lsv Mandate creation failed',
                      err,
                    );
                    triggerErrorToast(
                      t('Something went wrong!'),
                      t('Failed to authorize LSV Mandate.'),
                    );
                    Clarity.setTag('pay', [
                      RedirectStatus.Failure,
                      paymentModuleT(PaymentModuleId.DirectDebitLSV),
                    ]);
                  });
              }}
            />
          }
        </div>
      </div>
    </PageLayout>
  );
}
